import { useEffect } from 'react'
import { Link } from 'react-router'
import { Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { SidebarContent } from '@/components/ui/sidebar'
import { ModelPicker } from '@/components/generate/ModelPicker'
import { PromptInput } from '@/components/generate/PromptInput'
import { AspectRatioPicker } from '@/components/generate/AspectRatioPicker'
import { DurationPicker } from '@/components/generate/DurationPicker'
import { ResolutionPicker } from '@/components/generate/ResolutionPicker'
import { NumImagesPicker } from '@/components/generate/NumImagesPicker'
import { useGenerateWorkspace } from '@/context/GenerateWorkspaceContext'

export function GenerateFormSidebar() {
  const {
    providerName,
    keysLoading,
    keyFetching,
    hasKey,
    modelsLoading,
    models,
    modelId,
    setModelId,
    isVideo,
    providerId,
    prompt,
    setPrompt,
    numImages,
    setNumImages,
    maxImages,
    aspectRatio,
    setAspectRatio,
    durationSeconds,
    setDurationSeconds,
    durationOptions,
    resolution,
    setResolution,
    resolutionOptions,
    busy,
    handleGenerate,
  } = useGenerateWorkspace()

  // When resolution requires 8 s (1080p/4K), auto-correct duration if it's shorter.
  // WHY: Veo 3.1 API rejects durationSeconds < 8 when resolution is 1080p or 4K.
  useEffect(() => {
    if (
      (resolution === '1080p' || resolution === '4k') &&
      (durationSeconds === 4 || durationSeconds === 6)
    ) {
      setDurationSeconds(8)
    }
  }, [resolution, durationSeconds, setDurationSeconds])

  const disabledDurations = resolution === '1080p' || resolution === '4k' ? [4, 6] : []
  const isLoading = keysLoading || keyFetching || (modelsLoading && models.length === 0)

  return (
    <SidebarContent>
      <div
        className={cn(
          'flex flex-col gap-5 p-4 transition-opacity duration-150',
          isLoading && 'pointer-events-none select-none opacity-0',
        )}
      >
        {!hasKey && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-400">
            No {providerName} key.{' '}
            <Link to="/apps/settings" className="underline underline-offset-2 hover:text-amber-300">
              Add in Settings
            </Link>
          </div>
        )}

        <ModelPicker models={models} value={modelId} onChange={setModelId} />

        <PromptInput value={prompt} onChange={setPrompt} disabled={busy} />

        <AspectRatioPicker
          value={aspectRatio}
          onChange={setAspectRatio}
          options={isVideo ? ['16:9', '9:16'] : ['1:1', '16:9', '9:16', '4:3', '3:4']}
        />

        {!isVideo && maxImages > 1 && (
          <NumImagesPicker value={numImages} onChange={setNumImages} max={maxImages} />
        )}

        {/* WHY: Replicate duration is model-specific input, not a standard VideoRequest
            field. Wire per-model when Replicate video nodes are added. */}
        {isVideo && providerId === 'google-gemini' && durationOptions !== null && (
          <DurationPicker
            options={durationOptions}
            value={durationSeconds}
            onChange={setDurationSeconds}
            disabledOptions={disabledDurations}
          />
        )}

        {isVideo && resolutionOptions !== null && (
          <ResolutionPicker
            options={resolutionOptions}
            value={resolution}
            onChange={setResolution}
          />
        )}

        <Button
          variant="cta"
          onClick={handleGenerate}
          disabled={!hasKey || keyFetching || !modelId || !prompt.trim() || busy}
          className="w-full"
        >
          {busy ? 'Generating…' : 'Generate'}
        </Button>

        <Link
          to="/apps/settings"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <Settings className="h-3.5 w-3.5" />
          Manage API Keys
        </Link>
      </div>
    </SidebarContent>
  )
}
